import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  Dialog as FluentDialog,
  DialogBody,
  DialogSurface,
  Tooltip as FluentTooltip,
} from "@fluentui/react-components";

const cx = (...c: (string | false | undefined | null)[]) => c.filter(Boolean).join(" ");

export interface DialogProps {
  open: boolean;
  title: string;
  description?: string;
  size?: "md" | "lg";
  /** destructive confirm: Esc and the scrim do not close it while `busy` */
  busy?: boolean;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
}

/** Modal: focus trap, Esc closes, focus returns to the opener (Fluent Dialog); `surface-raised`, radius-xl, elevation-4. */
export function Dialog({
  open,
  title,
  description,
  size = "md",
  busy,
  onClose,
  children,
  footer,
}: DialogProps) {
  const id = useId();
  return (
    <FluentDialog open={open} modalType="modal" onOpenChange={(_e, d) => !d.open && !busy && onClose()}>
      <DialogSurface
        aria-labelledby={`${id}-t`}
        aria-describedby={description ? `${id}-d` : undefined}
        className={cx("gp-dialog", size === "lg" && "gp-dialog--lg")}
        style={{ padding: 0 }}
      >
        <DialogBody style={{ display: "flex", flexDirection: "column", gap: 0, padding: 0 }}>
          <div className="gp-dialog-head">
            <h2 id={`${id}-t`} className="t-title-sm">
              {title}
            </h2>
            {description ? (
              <p id={`${id}-d`} className="t-body gp-muted" style={{ margin: "var(--space-4) 0 0" }}>
                {description}
              </p>
            ) : null}
          </div>
          <div className="gp-dialog-body">{children}</div>
          {footer ? <div className="gp-dialog-foot">{footer}</div> : null}
        </DialogBody>
      </DialogSurface>
    </FluentDialog>
  );
}

export function Tooltip({ content, children }: { content: string; children: React.ReactElement }) {
  return (
    <FluentTooltip content={content} relationship="description" showDelay={500}>
      {children}
    </FluentTooltip>
  );
}

export interface TabItem {
  key: string;
  label: string;
  count?: number | undefined;
}

/** Underline tabs: tablist semantics, arrow keys move between tabs (04 Tabs). */
export function Tabs({
  label,
  tabs,
  value,
  onChange,
}: {
  label: string;
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const move = (e: KeyboardEvent, i: number) => {
    const dir = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const n = (i + dir + tabs.length) % tabs.length;
    refs.current[n]?.focus();
    onChange(tabs[n]!.key);
  };
  return (
    <div className="gp-tabs" role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.key}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          className="gp-tab"
          aria-selected={t.key === value}
          tabIndex={t.key === value ? 0 : -1}
          onClick={() => onChange(t.key)}
          onKeyDown={(e) => move(e, i)}
        >
          {t.label}
          {t.count !== undefined ? <span className="gp-count t-caption">{t.count}</span> : null}
        </button>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ toast

interface ToastItem {
  id: number;
  kind: "success" | "error";
  text: string;
}
interface ToastApi {
  success: (text: string) => void;
  error: (text: string) => void;
}
const ToastContext = createContext<ToastApi | null>(null);

/** Bottom-left toasts: success auto-dismisses after 6 s, errors stay until closed (04 Toast). */
export function ToastProvider({ children, closeLabel }: { children: ReactNode; closeLabel: string }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);
  const dismiss = useCallback((id: number) => setItems((l) => l.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (kind: ToastItem["kind"], text: string) => {
      const id = ++counter.current;
      setItems((l) => [...l, { id, kind, text }]);
      if (kind === "success") setTimeout(() => dismiss(id), 6000);
    },
    [dismiss],
  );
  const api = useRef<ToastApi>({ success: (t) => push("success", t), error: (t) => push("error", t) });
  api.current = { success: (t) => push("success", t), error: (t) => push("error", t) };
  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div className="gp-toasts">
        {items.map((t) => (
          <div
            key={t.id}
            className={cx("gp-toast", t.kind === "error" && "gp-toast--error")}
            role={t.kind === "error" ? "alert" : "status"}
          >
            <span className="t-body">{t.text}</span>
            <button type="button" className="gp-btn gp-btn--ghost" onClick={() => dismiss(t.id)}>
              {closeLabel}
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const c = useContext(ToastContext);
  if (!c) throw new Error("ToastProvider is missing");
  return c;
}
